import { toast } from "react-toastify";
import {
  Budget,
  ItemTypeListPerCalcOption,
  versionInfo,
} from "../../types/budget/budget";
import {
  DefaultTax,
  initialServiceData,
  ServiceData,
  ServiceValues,
} from "../../types/budget/service-item";
import {
  patchIssIofAppliedTaxesForCurrency,
  syncBudgetServicesAppliedTaxesIssIofFromBaseValues,
  syncMainAppliedTaxesIssIofFromBaseValues,
} from "../../utils/appliedTaxesByCurrency";
import { uuid } from "../../utils/utils";
import { shouldSkipBudgetRecalcForMetadataOnlyServiceUpdate } from "../../utils/budgetServiceCalcComparison";
import { initialItemTypeList } from "./consts";
import {
  calculateAndMapService,
  calculateDetailedTotals,
  calculateDetailedTotalsNegotiation,
  ExchangeData,
  TaxData,
  Totals,
} from "./calc-methods";

export interface ActiveServiceAction {
  type:
    | "CHANGE_CALC_TYPE"
    | "NO_ACTIVE_BUDGET"
    | "NEW_ACTIVE_BUDGET"
    | "ADD_SUPPLIER"
    | "UPDATE_SUPPLIER_LIST"
    | "ADD_BUDGET_ITEM"
    | "DELETE_SERVICE"
    | "ADD_SERVICE"
    | "YES_NO_SWITCH_CHANGE"
    | "UPDATE_SERVICE"
    | "UPDATE_FROM_API"
    | "UPDATE_CALC_VALUES"
    | "DELETE_BUDGET_ITEM"
    | "DELETE_SUPPLIER"
    | "CREATE_PAYMENT_INFO"
    | "UPDATE_PAYMENT_INFO"
    | "UPDATE_ANEX_FILES"
    | "CREATE_ANEX_FILES"
    | "UPDATE_BASE_CALC_VALUES"
    | "INIT_BASE_VALUES"
    | "UPDATE_BASE_VALUES_EXCHANGE"
    | "INIT_MAIN_APPLIED_TAXES"
    | "UPDATE_MAIN_APPLIED_TAXES"
    | "NEW_GLOBAL_TAX_DATA"
    | "REORDER_SERVICES";
  payload: any;
  versionInfo?: versionInfo;
}

/** Verifica se um TaxData possui ao menos uma taxa com valor parametrizado (> 0). */
function hasAnyNonZeroTax(taxData: Record<string, any> | undefined): boolean {
  if (!taxData) return false;
  return Object.values(taxData).some(
    (taxItem: any) =>
      taxItem &&
      typeof taxItem === "object" &&
      "value" in taxItem &&
      taxItem.value > 0
  );
}

export function activeServiceReducer(
  state: Budget,
  action: ActiveServiceAction
) {
  let updatedBudget = { ...state };
  switch (action.type) {
    case "CHANGE_CALC_TYPE":
      updatedBudget.itemTypeList = {
        ...updatedBudget.itemTypeList,
        [action.payload.key]: action.payload.value,
      };
      toast.success("Tipo de calculo mudado para:" + action.payload.value);
      break;
    case "NO_ACTIVE_BUDGET":
      updatedBudget = {} as Budget;
      break;
    case "NEW_ACTIVE_BUDGET":
      updatedBudget = action.payload;
      break;
    case "ADD_SUPPLIER":
      updatedBudget.supplierList.push({
        supplierId: "",
        calcOption: action.payload.calcOption,
      });
      break;
    case "NEW_GLOBAL_TAX_DATA":
      const oldTaxData = updatedBudget.budgetWideAppliedTaxes.find(
        (el) => el.version === action.payload.newBudgetWideAppliedTaxes.version
      );
      if (!oldTaxData) {
        updatedBudget.budgetWideAppliedTaxes.push(
          action.payload.newBudgetWideAppliedTaxes
        );
      } else {
        updatedBudget.budgetWideAppliedTaxes =
          updatedBudget.budgetWideAppliedTaxes.map((el) => {
            if (
              el.version === action.payload.newBudgetWideAppliedTaxes.version
            ) {
              return action.payload.newBudgetWideAppliedTaxes;
            }
            return el;
          });
      }

      const applyExchange = shouldApplyExchange(
        action.payload.newBudgetWideAppliedTaxes.exchange,
        oldTaxData?.exchange
      );

      updatedBudget.baseValues = updatedBudget.baseValues.map((el) => {
        if (el.version === action.payload.newBudgetWideAppliedTaxes.version) {
          const newTaxData = action.payload.newBudgetWideAppliedTaxes.taxData;
          const previousGlobalTaxData = oldTaxData?.taxData;
          const updatedTaxData = { ...el.taxData };

          // Propaga o valor da taxa de Totais sempre que ele MUDOU em relação
          // ao valor global anterior — inclusive quando foi zerado (ex.: 10 -> 0).
          // Assim "zerar uma taxa em Totais" reflete em todas as categorias, sem
          // sobrescrever campos que o usuário não alterou nesta ação (ex.: só câmbio).
          Object.keys(newTaxData).forEach((key) => {
            const taxItem = newTaxData[key];
            if (
              !taxItem ||
              typeof taxItem !== "object" ||
              !("value" in taxItem)
            )
              return;

            const previousItem = previousGlobalTaxData?.[key as keyof TaxData];
            const previousValue =
              previousItem &&
              typeof previousItem === "object" &&
              "value" in previousItem
                ? previousItem.value
                : 0;

            if (taxItem.value === previousValue) return;

            // Taxa customizada na categoria (diferente do que Totais tinha
            // antes) é preservada; as demais taxas da categoria seguem Totais.
            // Ex.: categoria com Taxa de Serviço 5% continua com 5% quando
            // Totais aplica 10%, mas recebe o Imposto aplicado em Totais.
            const categoryItem = el.taxData?.[key as keyof TaxData];
            const categoryValue =
              categoryItem &&
              typeof categoryItem === "object" &&
              "value" in categoryItem
                ? categoryItem.value || 0
                : 0;
            const isCustomizedInCategory =
              categoryValue !== (previousValue || 0) ||
              (categoryValue !== 0 &&
                (categoryItem as any)?.typeTax !==
                  (previousItem as any)?.typeTax);
            if (isCustomizedInCategory) return;

            updatedTaxData[key as keyof TaxData] = taxItem;
          });

          el.taxData = updatedTaxData;
          // O câmbio segue o comportamento anterior: categoria com taxas
          // customizadas à mão não recebe o câmbio de Totais.
          if (applyExchange && !(el as any).taxDataOverride)
            el.exchange = action.payload.newBudgetWideAppliedTaxes.exchange;
        }
        return el;
      });

      break;
    case "UPDATE_SUPPLIER_LIST":
      // atualiza supplierlist
      const index = updatedBudget.supplierList.findIndex(
        (el) =>
          el.supplierId === action.payload.currentId &&
          el.calcOption === action.payload.calcOption
      );
      updatedBudget.supplierList[index] = {
        supplierId: action.payload.newId,
        calcOption: action.payload.calcOption,
      };
      // atualiza serviços do supplier com novo id
      updatedBudget.budgetServices = updatedBudget.budgetServices.map(
        (service) => {
          if (service.supplierId === action.payload.currentId) {
            service.supplierId = action.payload.newId;
            service.supplierName = action.payload.newName;
          }
          return service;
        }
      );
      // atualiza addedItemTypeList
      updatedBudget.addedItemTypeList = updatedBudget.addedItemTypeList.map(
        (item) => {
          if (item.supplierId === action.payload.currentId) {
            item.supplierId = action.payload.newId;
          }
          return item;
        }
      );
      updatedBudget.baseValues = updatedBudget.baseValues.map((el) => {
        if (
          el.supplierId === action.payload.currentId &&
          el.calcOption === action.payload.calcOption
        ) {
          el.supplierId = action.payload.newId;
        }
        return el;
      });

      toast.success("Lista de fornecedores atualizada.");
      break;
    case "DELETE_SUPPLIER":
      // Ao deletar um fornecedor, todos serviços dele também serão apagados!
      updatedBudget.addedItemTypeList = updatedBudget.addedItemTypeList.filter(
        (el) =>
          !(
            el.supplierId === action.payload.supplierId &&
            el.calcOption === action.payload.calcOption
          )
      );
      updatedBudget.budgetServices = updatedBudget.budgetServices.filter(
        (el) =>
          !(
            el.supplierId === action.payload.supplierId &&
            el.calcOption === action.payload.calcOption
          )
      );

      updatedBudget.supplierList = updatedBudget.supplierList.filter(
        (el) =>
          !(
            el.supplierId === action.payload.supplierId &&
            el.calcOption === action.payload.calcOption
          )
      );

      // precisa comparar versões também
      updatedBudget.baseValues = updatedBudget.baseValues.filter(
        (el) =>
          !(
            el.supplierId === action.payload.supplierId &&
            el.calcOption === action.payload.calcOption &&
            el.version === action.versionInfo?.version
          )
      );
      break;
    case "ADD_BUDGET_ITEM":
      if (state.addedItemTypeList?.includes(action.payload)) {
        toast.warn("Tipo já existente");
        return state;
      }
      updatedBudget.addedItemTypeList.push(action.payload);
      break;
    case "DELETE_SERVICE":
      const indexDeleted = state.budgetServices.findIndex(
        (el) => el._id === action.payload
      );
      updatedBudget.budgetServices = updatedBudget.budgetServices.filter(
        (el, index) => index !== indexDeleted
      );
      toast.success("Serviço removido");
      break;
    case "ADD_SERVICE":
      // Busca as taxas aplicadas da categoria (mainAppliedTaxes) se existirem
      // Se não existir configuração da categoria, usa o padrão do sistema (todas as taxas habilitadas)
      const categoryMainAppliedTaxesService = state.mainAppliedTaxes?.find(
        (el) =>
          el.supplierId === action.payload.supplierId &&
          el.category === action.payload.type &&
          el.calcOption === action.payload.calcOption
      );

      // Define as appliedTaxes herdadas da categoria ou usa o padrão do sistema
      const inheritedAppliedTaxesService =
        categoryMainAppliedTaxesService?.appliedTaxes || {
          serviceTax: true,
          iss: true,
          cbs: true,
          ibs: true,
          tribute: true,
          supplier_tribute: true,
          localTax: true,
          tourismTax: true,
          fee: true,
          iof: true,
          markup: true,
          overhead: true,
          commission: true,
          incentive: true,
          doubleCharged: true,
        };

      // Busca os valores de taxa (taxData) já parametrizados para a categoria (herdados
      // de Totais na criação da categoria, ou customizados manualmente) para que os
      // serviços vinculados também nasçam com esses valores.
      const categoryBaseValueService = state.baseValues?.find(
        (el) =>
          el.supplierId === action.payload.supplierId &&
          el.category === action.payload.type &&
          el.calcOption === action.payload.calcOption
      );
      const inheritedTaxDataService = categoryBaseValueService?.taxData
        ? {
            ...initialServiceData.budgetValues.taxData,
            ...categoryBaseValueService.taxData,
          }
        : { ...initialServiceData.budgetValues.taxData };

      const newServiceItem = {
        ...initialServiceData,
        type: action.payload.type,
        supplierId: action.payload.supplierId,
        supplierName: action.payload.supplierName,
        calcOption: action.payload.calcOption,
        _id: uuid(),
        budgetValues: {
          ...initialServiceData.budgetValues,
          appliedTaxes: { ...inheritedAppliedTaxesService },
          taxData: {
            ...inheritedTaxDataService,
            tariff: {
              ...inheritedTaxDataService.tariff,
              typeTax: "DAILY",
            },
          },
        },
      };

      // If insertAtIndex is provided, insert at that position; otherwise, add at the end
      if (
        action.payload.insertAtIndex !== undefined &&
        action.payload.insertAtIndex >= 0
      ) {
        // Create a new array to trigger React re-render
        const newServices = [...updatedBudget.budgetServices];
        newServices.splice(action.payload.insertAtIndex, 0, newServiceItem);
        updatedBudget.budgetServices = newServices;
      } else {
        updatedBudget.budgetServices = [
          ...updatedBudget.budgetServices,
          newServiceItem,
        ];
      }

      // Toast lives in `dispatchAddService`. A replay of this reducer showed
      // "Serviço adicionado" again without leaving another row on screen.
      break;
    case "REORDER_SERVICES":
      const { oldIndex, newIndex } = action.payload;
      const reorderedServices = [...updatedBudget.budgetServices];
      const [movedService] = reorderedServices.splice(oldIndex, 1);
      reorderedServices.splice(newIndex, 0, movedService);
      updatedBudget.budgetServices = reorderedServices;
      toast.success("Serviço reordenado");
      break;
    case "YES_NO_SWITCH_CHANGE":
      if (
        !!updatedBudget.itemTypeList &&
        !!updatedBudget.itemTypeList[
          action.payload.activeCalcOption as keyof ItemTypeListPerCalcOption
        ]
      ) {
        updatedBudget.itemTypeList[
          action.payload.activeCalcOption as keyof ItemTypeListPerCalcOption
        ][action.payload.type] = action.payload.newValue;
      } else if (
        !!updatedBudget.itemTypeList &&
        !updatedBudget.itemTypeList[
          action.payload.activeCalcOption as keyof ItemTypeListPerCalcOption
        ]
      ) {
        updatedBudget.itemTypeList = {
          ...updatedBudget.itemTypeList,
          [action.payload.activeCalcOption as keyof ItemTypeListPerCalcOption]:
            {
              [action.payload.type]: action.payload.newValue,
            },
        };
      } else {
        updatedBudget = {
          ...updatedBudget,
          itemTypeList: initialItemTypeList(),
        };
        updatedBudget.itemTypeList[
          action.payload.activeCalcOption as keyof ItemTypeListPerCalcOption
        ][action.payload.type] = action.payload.newValue;
      }
      toast.success("Lista de opções atualizada");
      break;
    case "UPDATE_SERVICE":
      const servicesList = updatedBudget.budgetServices.map((el) => {
        if (el._id === action.payload._id) {
          return action.payload;
        } else return el;
      });
      updatedBudget.budgetServices = servicesList;
      break;
    case "UPDATE_CALC_VALUES":
      const servicesListCalc = updatedBudget.budgetServices.map((el) => {
        if (el._id === action.payload._id) {
          return action.payload;
        } else return el;
      });
      updatedBudget.budgetServices = servicesListCalc;
      break;

    case "UPDATE_BASE_CALC_VALUES":
      const baseValueVersionService =
        action.versionInfo?.version ?? action.payload?.version ?? 0;
      const newBaseValues = updatedBudget.baseValues.map((el) => {
        if (
          el.supplierId === action.payload.supplierId &&
          el.category === action.payload.type &&
          el.calcOption === action.payload.calcOption &&
          el.version === baseValueVersionService
        ) {
          const updatedValue = {
            ...el,
            [action.payload?.field]: action.payload?.value,
          };

          // Se o campo alterado for exchange, verifica se deve marcar como override
          if (action.payload?.field === "exchange") {
            // Se o payload explicitamente define isOverride, usa esse valor
            // Isso permite que o resetToDefault force isOverride = false
            if (action.payload.value.hasOwnProperty("isOverride")) {
              updatedValue.isOverride = action.payload.value.isOverride;
            } else {
              // Caso contrário, marca como override pois foi editado manualmente
              updatedValue.isOverride = true;
            }
          }

          // Se o campo alterado for taxData, marca como customizado manualmente
          // para que futuras alterações de taxas em Totais não sobrescrevam esta categoria
          if (action.payload?.field === "taxData") {
            if (action.payload.value?.hasOwnProperty("taxDataOverride")) {
              (updatedValue as any).taxDataOverride =
                action.payload.value.taxDataOverride;
            } else {
              (updatedValue as any).taxDataOverride = true;
            }
          }

          return updatedValue;
        } else return el;
      });

      updatedBudget.baseValues = newBaseValues;

      if (
        action.payload?.field === "exchange" &&
        action.payload.value?.currency != null
      ) {
        const ccy = action.payload.value.currency as string;
        updatedBudget.mainAppliedTaxes = (
          updatedBudget.mainAppliedTaxes || []
        ).map((row) => {
          if (
            row.supplierId === action.payload.supplierId &&
            row.category === action.payload.type &&
            row.calcOption === action.payload.calcOption
          ) {
            return {
              ...row,
              appliedTaxes: patchIssIofAppliedTaxesForCurrency(
                row.appliedTaxes,
                ccy
              ),
            };
          }
          return row;
        });
        updatedBudget.budgetServices = (updatedBudget.budgetServices || []).map(
          (svc) => {
            if (
              svc.supplierId !== action.payload.supplierId ||
              svc.type !== action.payload.type ||
              svc.calcOption !== action.payload.calcOption
            ) {
              return svc;
            }
            const patchOne = (a: typeof svc.budgetValues.appliedTaxes) =>
              patchIssIofAppliedTaxesForCurrency(
                {
                  ...initialServiceData.budgetValues.appliedTaxes,
                  ...a,
                },
                ccy
              );
            const next = {
              ...svc,
              budgetValues: {
                ...svc.budgetValues,
                appliedTaxes: patchOne(svc.budgetValues?.appliedTaxes),
              },
            };
            if (
              Array.isArray(svc.negotiationValue) &&
              svc.negotiationValue.length
            ) {
              next.negotiationValue = svc.negotiationValue.map((nv) => ({
                ...nv,
                appliedTaxes: patchOne(
                  nv.appliedTaxes ?? svc.budgetValues?.appliedTaxes
                ),
              }));
            }
            return next;
          }
        );
      }
      break;

    case "INIT_BASE_VALUES": {
      // Usa o câmbio do evento se fornecido, caso contrário usa o padrão
      const exchangeToUse =
        action.payload?.eventExchange || baseValueDummy.exchange;
      const initVersion =
        action.versionInfo?.version ?? action.payload?.version ?? 0;

      // Nova categoria herda as taxas parametrizadas em Totais (budgetWideAppliedTaxes)
      // para a versão ativa, se houver alguma taxa com valor definido. Caso contrário,
      // a categoria nasce com as taxas zeradas (comportamento padrão).
      const globalTaxForVersion = state.budgetWideAppliedTaxes?.find(
        (el) => el.version === initVersion
      );
      const inheritedTaxData = hasAnyNonZeroTax(globalTaxForVersion?.taxData)
        ? { ...baseValueDummy.taxData, ...globalTaxForVersion?.taxData }
        : { ...baseValueDummy.taxData };

      updatedBudget.baseValues = [
        ...updatedBudget.baseValues,
        {
          ...baseValueDummy,
          taxData: inheritedTaxData,
          calcOption: action.payload?.calcOption,
          category: action.payload?.type,
          supplierId: action.payload?.supplierId,
          version: initVersion,
          exchange: exchangeToUse,
          isOverride: false, // Nova categoria sempre começa sem override
          taxDataOverride: false, // Nova categoria sempre começa sem customização manual de taxas
        } as any,
      ];
      break;
    }

    case "UPDATE_BASE_VALUES_EXCHANGE":
      if (
        action.payload?.eventExchange &&
        updatedBudget.baseValues &&
        updatedBudget.baseValues.length > 0
      ) {
        const forceUpdate = action.payload?.forceUpdate || false;
        updatedBudget.baseValues = updatedBudget.baseValues.map((baseValue) => {
          // Se forceUpdate=false e tem override manual, não altera
          if (!forceUpdate && baseValue.isOverride) {
            return baseValue;
          }

          // Se forceUpdate=true (vindo de Dados Cadastrais), atualiza TODOS
          return {
            ...baseValue,
            exchange: {
              date: action.payload.eventExchange.date,
              currency: action.payload.eventExchange.currency,
              value: action.payload.eventExchange.value,
            },
            isOverride: false, // Remove override ao sincronizar com evento
          };
        });

        updatedBudget.mainAppliedTaxes =
          syncMainAppliedTaxesIssIofFromBaseValues(
            updatedBudget.mainAppliedTaxes || [],
            updatedBudget.baseValues || []
          );
        updatedBudget.budgetServices =
          syncBudgetServicesAppliedTaxesIssIofFromBaseValues(
            updatedBudget.budgetServices || [],
            updatedBudget.baseValues || []
          ) as ServiceData[];
      }
      break;

    case "UPDATE_PAYMENT_INFO":
      const newPaymentInfoArr = updatedBudget.paymentSolicitation.map((el) => {
        if (
          el.supplierId === action.payload.supplierId &&
          el.type === action.payload.type &&
          el.calcOption === action.payload.calcOption
        ) {
          return action.payload;
        }
        return el;
      });
      updatedBudget.paymentSolicitation = newPaymentInfoArr;
      toast.success("Solicitação de pagamento atualizada!");

      break;
    case "CREATE_PAYMENT_INFO":
      updatedBudget.paymentSolicitation.push({
        ...action.payload,
        _id: uuid(),
      });
      toast.success("Solicitação de pagamento criada!");

      break;
    case "UPDATE_ANEX_FILES":
      const newFilesArr = updatedBudget.paymentSolicitation.map((el) => {
        if (
          el.supplierId === action.payload.supplierId &&
          el.type === action.payload.type &&
          el.calcOption === action.payload.calcOption
        ) {
          return action.payload;
        }
        return el;
      });
      updatedBudget.paymentSolicitation = newFilesArr;
      toast.success("Solicitação de pagamento atualizada!");

      break;
    case "CREATE_ANEX_FILES":
      updatedBudget.paymentSolicitation.push(action.payload.paymentInfo);
      toast.success("Solicitação de pagamento criada!");

      break;
    case "UPDATE_FROM_API":
      updatedBudget = action.payload;
      break;
    case "DELETE_BUDGET_ITEM":
      // Ao deletar um budgetItem, todos serviços dele também serão apagados!
      updatedBudget.addedItemTypeList = updatedBudget.addedItemTypeList.filter(
        (el) =>
          !(
            el.supplierId === action.payload.supplierId &&
            el.type === action.payload.type &&
            el.calcOption === action.payload.calcOption
          )
      );
      updatedBudget.budgetServices = updatedBudget.budgetServices.filter(
        (el) =>
          !(
            el.supplierId === action.payload.supplierId &&
            el.type === action.payload.type &&
            el.calcOption === action.payload.calcOption
          )
      );
      updatedBudget.baseValues = updatedBudget.baseValues.filter(
        (el) =>
          !(
            el.supplierId === action.payload.supplierId &&
            el.category === action.payload.type &&
            el.calcOption === action.payload.calcOption &&
            el.version === action.versionInfo?.version
          )
      );

      break;
    case "UPDATE_MAIN_APPLIED_TAXES": {
      updatedBudget.mainAppliedTaxes = updatedBudget.mainAppliedTaxes.map(
        (el) => {
          if (
            el.supplierId === action.payload.supplierId &&
            el.category === action.payload.category &&
            el.calcOption === action.payload.calcOption
          ) {
            return action.payload;
          }
          return el;
        }
      );

      // Sempre sincroniza: não há como distinguir de forma confiável uma
      // customização manual real de um serviço que só ficou desatualizado
      // em relação ao padrão (ex.: taxa nunca sincronizada antes). Alterar
      // "Taxas padrão" deve refletir em todos os serviços da categoria.
      const syncAppliedTaxesIfInherited = (
        serviceValues: ServiceValues
      ): ServiceValues => ({
        ...serviceValues,
        appliedTaxes: action.payload.appliedTaxes,
      });

      updatedBudget.budgetServices = updatedBudget.budgetServices.map((el) => {
        if (
          el.supplierId !== action.payload.supplierId ||
          el.type !== action.payload.category ||
          el.calcOption !== action.payload.calcOption
        ) {
          return el;
        }

        const newService = { ...el };
        if (action.payload.version === 0) {
          newService.budgetValues = syncAppliedTaxesIfInherited(
            newService.budgetValues
          );
        } else {
          newService.negotiationValue = [
            ...(newService.negotiationValue ?? []),
          ];
          const currentNegotiationValue =
            newService.negotiationValue[action.payload.version - 1] ??
            newService.budgetValues;
          newService.negotiationValue[action.payload.version - 1] =
            syncAppliedTaxesIfInherited(currentNegotiationValue);
        }
        return newService;
      });
      break;
    }
    case "INIT_MAIN_APPLIED_TAXES":
      const newMainAppliedTaxes = {
        appliedTaxes: {
          serviceTax: true,
          iss: true,
          cbs: true,
          ibs: true,
          tribute: true,
          supplier_tribute: true,
          localTax: true,
          tourismTax: true,
          fee: true,
          iof: true,
          markup: true,
          overhead: true,
          commission: true,
          incentive: true,
          doubleCharged: true,
        },
        calcOption: action.payload?.calcOption,
        category: action.payload?.category,
        supplierId: action.payload?.supplierId,
        version: action.payload?.version,
      };
      updatedBudget.mainAppliedTaxes?.length > 0
        ? (updatedBudget.mainAppliedTaxes = [
            ...updatedBudget.mainAppliedTaxes,
            newMainAppliedTaxes,
          ])
        : (updatedBudget.mainAppliedTaxes = [newMainAppliedTaxes]);
      break;
    default:
      break;
  }

  // para qualquer uma dessas ações versionInfo deve ser fornecida
  const actionCalc = [
    "UPDATE_SUPPLIER_LIST",
    "UPDATE_BASE_VALUES_EXCHANGE", // Dispara recálculo quando câmbio das categorias muda
    "DELETE_SERVICE",
    "UPDATE_SERVICE",
    "DELETE_BUDGET_ITEM",
    "DELETE_SUPPLIER",
    "UPDATE_BASE_CALC_VALUES",
    "UPDATE_CALC_VALUES",
    "UPDATE_MAIN_APPLIED_TAXES",
    "NEW_GLOBAL_TAX_DATA",
    // Mesma lacuna do BudgetContext (MMV-2511-X): categoria sem `baseValues`
    // na versão ativa recalcula com o dummy all-zero até alguém editar algo.
    // `INIT_BASE_VALUES` cria a linha mas não recalculava em cima dela.
    "INIT_BASE_VALUES",
  ];
  let shouldRecalculateTotals = actionCalc.includes(action.type);
  if (
    action.type === "UPDATE_SERVICE" &&
    shouldRecalculateTotals &&
    shouldSkipBudgetRecalcForMetadataOnlyServiceUpdate(state, action.payload)
  ) {
    shouldRecalculateTotals = false;
  }

  if (shouldRecalculateTotals) {
    const updatedBudgetCalc = updateCalcValues(
      updatedBudget,
      action.versionInfo || { comparisonVersion: 0, version: 0, status: "OPEN" }
    );
    return updatedBudgetCalc;
  }

  // Garante que arrays e objetos sejam novos para React detectar mudanças
  return {
    ...updatedBudget,
    baseValues: updatedBudget.baseValues
      ? updatedBudget.baseValues.map((bv) => ({ ...bv }))
      : [],
    budgetServices: updatedBudget.budgetServices
      ? [...updatedBudget.budgetServices]
      : [],
  };
}
function shouldApplyExchange(
  exchange: ExchangeData,
  compareExchange: ExchangeData | undefined
) {
  return (
    (exchange.value !== 0 && exchange.value !== compareExchange?.value) ||
    exchange.date !== compareExchange?.date ||
    exchange.currency !== compareExchange?.currency ||
    compareExchange === undefined
  );
}

function updateCalcValues(updatedBudget: Budget, versionInfo: versionInfo) {
  const budgetCalculated = updatedBudget;
  budgetCalculated.budgetServices = budgetCalculated.budgetServices.map(
    (service) => {
      return calculateAndMapService(service, budgetCalculated, versionInfo);
    }
  );
  const extras =
    updatedBudget.budgetWideAppliedTaxes?.find(
      (el) => el.version === versionInfo.version
    )?.taxData.extras || zeroTax;

  switch (true) {
    case versionInfo.status === "APPROVED":
      //closing
      // MMV-2419-10: filtra por `versions` (igual à negociação) para não somar no total de
      // fechamento serviços excluídos nesta etapa (senão gera falso "aumento de escopo").
      budgetCalculated.closingValuesTotal = calculateDetailedTotals(
        budgetCalculated.budgetServices.filter((service) =>
          (service.versions ?? []).includes(versionInfo.version)
        ),
        "closingValues",
        extras
      );
      break;
    case versionInfo.status === "NEGOTIATE":
      // negotiation
      const hasIndex =
        budgetCalculated.negociationValuesTotal.length === versionInfo.version;

      if (hasIndex) {
        budgetCalculated.negociationValuesTotal[versionInfo.version - 1] =
          calculateDetailedTotalsNegotiation(
            budgetCalculated.budgetServices,
            versionInfo,
            extras
          );
      } else {
        budgetCalculated.negociationValuesTotal.push(
          calculateDetailedTotalsNegotiation(
            budgetCalculated.budgetServices,
            versionInfo,
            extras
          )
        );
      }
      break;

    case versionInfo.version === 0 && versionInfo.status !== "APPROVED":
    default:
      // budget
      budgetCalculated.budgetValuesTotal = calculateDetailedTotals(
        budgetCalculated.budgetServices,
        "budgetValues",
        extras
      );
      break;
  }

  return updatedBudget;
}
export const zeroTax = {
  typeTax: "PERCENTAGE",
  value: 0,
  currencyValue: 0,
  valueBRL: 0,
  typeCalculate: "",
};

export const baseValueDummy = {
  version: 0,
  supplierId: "",
  category: "", // substitua com 'BudgetItemType.EXEMPLO' se tiver enums definidos
  calcOption: "A", // substitua com 'CalcOption.EXEMPLO' se tiver enums definidos
  exchange: { currency: "BRL", date: "", value: 1 }, // preencha conforme a estrutura real de ExchangeData
  taxData: {
    tariff: { ...zeroTax },
    serviceTax: { ...zeroTax },
    localTax: { ...zeroTax },
    tourismTax: { ...zeroTax },
    iof: { ...zeroTax },
    fee: { ...zeroTax },
    iss: { ...zeroTax },
    cbs: { ...zeroTax },
    ibs: { ...zeroTax },
    typeNfMm: "",
    overhead: { ...zeroTax },
    markup: { ...zeroTax },
    commission: { ...zeroTax },
    incentive: { ...zeroTax },
    tribute: { ...zeroTax },
    doubleCharged: { ...zeroTax },
  },
  descriptions: {
    composition: "",
    cancelPolicy: "",
    paymentMethod: "",
    commercialConditions: false,
  },
};
